import {useState} from "react";
import {Button} from "../../../basic/button";
import {Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle} from "../../../basic/dialog";
import {toast} from "sonner";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {useErrorHandler} from "../../../../hooks/useErrorHandler";
import {ProductSelector} from "./ProductSelector";
import {TeamSelector} from "./TeamSelector";
import {LicenseForm} from "./LicenseForm";
import {useCreateLicenseMutation, useGetTeamsQuery} from "../../../../store/generated/api";

interface CreateLicenseDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export function CreateLicenseDialog({open, onOpenChange}: CreateLicenseDialogProps) {
    const {t} = useTranslationWithKeys();
    const {showErrorToast, handleApiError} = useErrorHandler();
    const [createLicense, {isLoading}] = useCreateLicenseMutation();
    const [licenseData, setLicenseData] = useState({
        productId: "",
        teamId: "",
        quantity: 1,
        expiration: ""
    });

    const {data: teamsData} = useGetTeamsQuery({
        page: 0,
        size: 100,
        search: licenseData.teamId
    });

    const teamName = teamsData?.teams?.find(team => team.id === licenseData.teamId)?.name || licenseData.teamId;

    const handleCreateLicense = async () => {
        if (!licenseData.productId) {
            showErrorToast({message: t('admin.licenses.createLicenseDialog.errors.selectProduct')});
            return;
        }

        if (!licenseData.quantity || licenseData.quantity < 1) {
            showErrorToast({message: t('admin.licenses.createLicenseDialog.errors.quantityMin')});
            return;
        }

        if (!licenseData.teamId) {
            showErrorToast({message: t('admin.licenses.createLicenseDialog.errors.selectTeam')});
            return;
        }

        try {
            const licenseRequest = {
                productId: licenseData.productId,
                teamId: licenseData.teamId,
                quantity: licenseData.quantity,
                ...(licenseData.expiration && {expires: new Date(licenseData.expiration).toISOString()})
            };

            await createLicense({vwpCreateLicenseRequest: licenseRequest}).unwrap();

            toast.success(t('admin.licenses.createLicenseDialog.success', {
                teamName
            }));

            onOpenChange(false);
            setLicenseData({
                productId: "",
                teamId: "",
                quantity: 1,
                expiration: ""
            });
        } catch (error) {
            handleApiError(error, {fallbackMessage: t('common.error.unknown')});
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="w-[90vw] max-w-7xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{t('admin.licenses.createLicenseDialog.title')}</DialogTitle>
                    <DialogDescription>
                        {t('admin.licenses.createLicenseDialog.description')}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 py-4">
                    <div className="grid md:grid-cols-2 gap-6">
                        <ProductSelector
                            value={licenseData.productId}
                            onChange={(value) => setLicenseData({...licenseData, productId: value})}
                        />

                        <LicenseForm
                            quantity={licenseData.quantity}
                            expiration={licenseData.expiration}
                            onQuantityChange={(quantity) => setLicenseData({...licenseData, quantity})}
                            onExpirationChange={(expiration) => setLicenseData({...licenseData, expiration})}
                        />

                        <TeamSelector
                            value={licenseData.teamId}
                            onChange={(value) => setLicenseData({...licenseData, teamId: value})}
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        {t('common.cancel')}
                    </Button>
                    <Button onClick={handleCreateLicense} disabled={isLoading}>
                        {isLoading ? t('common.loading') : t('admin.licenses.createLicenseDialog.createButton')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}